import { useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'
import type { AccountingSnapshot } from '../types'
import styles from './ConfirmPenaltyModal.module.css'

type ConfirmPenaltyModalProps = {
  accounting: AccountingSnapshot
  isSaving:   boolean
  onConfirm:  () => void
  onCancel:   () => void
}

export function ConfirmPenaltyModal({
  accounting,
  isSaving,
  onConfirm,
  onCancel,
}: ConfirmPenaltyModalProps) {
  // ESC to cancel
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isSaving) onCancel()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isSaving, onCancel])

  const {
    outgoingTotal,
    freeTransfersUsed,
    eliminationCreditsUsed,
    byeCreditsUsed,
    paidExtras,
    penaltyPoints,
  } = accounting

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={isSaving ? undefined : onCancel}
        aria-hidden="true"
      />

      <div
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-penalty-title"
      >
        <div className={styles.header}>
          <div className={styles.iconWrap} aria-hidden="true">
            <AlertTriangle size={18} strokeWidth={2.5} />
          </div>
          <h2 id="confirm-penalty-title" className={styles.title}>
            Confirmar transferência paga
          </h2>
        </div>

        <div className={styles.body}>
          <p className={styles.intro}>
            Você está fazendo mais transferências do que as gratuitas disponíveis.
            Cada transferência paga custa <strong>−5 pts</strong> nesta rodada.
          </p>

          <div className={styles.breakdown}>
            <div className={styles.row}>
              <span className={styles.rowLabel}>Total de transferências</span>
              <span className={styles.rowValue}>{outgoingTotal}</span>
            </div>

            {eliminationCreditsUsed > 0 && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>Créditos de eliminação usados</span>
                <span className={clsx(styles.rowValue, styles.credit)}>
                  −{eliminationCreditsUsed}
                </span>
              </div>
            )}

            {byeCreditsUsed > 0 && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>Créditos de folga usados</span>
                <span className={clsx(styles.rowValue, styles.credit)}>
                  −{byeCreditsUsed}
                </span>
              </div>
            )}

            <div className={styles.row}>
              <span className={styles.rowLabel}>Transferências gratuitas usadas</span>
              <span className={clsx(styles.rowValue, styles.free)}>
                {freeTransfersUsed} / 3
              </span>
            </div>

            <hr className={styles.divider} />

            <div className={styles.row}>
              <span className={styles.rowLabel}>Transferências pagas</span>
              <span className={clsx(styles.rowValue, styles.paid)}>
                {paidExtras} × −5 pts
              </span>
            </div>
          </div>

          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Multa total</span>
            <span className={styles.totalValue}>{penaltyPoints} pts</span>
          </div>

          <p className={styles.reversalNote}>
            Se você remover um jogador trazido por transferência paga antes do fechamento
            da janela, a multa é reembolsada.
          </p>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={isSaving}
            autoFocus
          >
            {isSaving && <span className={styles.spinner} aria-hidden="true" />}
            {isSaving ? 'Salvando…' : `Confirmar (${penaltyPoints} pts)`}
          </button>
        </div>
      </div>
    </>
  )
}
